import { get } from 'lodash-es';
import { createSelector } from 'reselect';

import { GuidService } from 'ui-components/services/guid-service';

const baseRequiredItemFields = ['description', 'quantity', 'unitPrice.value', 'originCountryCode'];

const getRequiredItemFields = (includeHarmonizedTariffCode = false) => {
  if (includeHarmonizedTariffCode) {
    return [...baseRequiredItemFields, 'harmonizedTariffCode'];
  }
  return baseRequiredItemFields;
};

export const emptyCustomsItem = ({ defaultCountryCode = 'US', currencyCode = 'USD', weightUnit = 'Ounces' } = {}) => ({
  description: '',
  weight: {
    value: undefined,
    unit: weightUnit,
  },
  sku: '',
  quantity: 0,
  unitPrice: {
    value: undefined,
    code: currencyCode,
  },
  harmonizedTariffCode: '',
  originCountryCode: defaultCountryCode,
  fulfillmentPlanItemId: '',
  declarationKey: GuidService.newGuid(),
});

const customsItemsWithDefaults = (customsItems = [], defaultCurrency, weightUnit) => {
  if (!customsItems) {
    return [];
  }

  return customsItems.map((customsItem) => {
    if (!customsItem) {
      return customsItem;
    }
    const customItemWithFallbacks = {
      ...customsItem,
      quantity: customsItem.quantity || 0,
      unitPrice: customsItem.unitPrice || {},
      weight: customsItem.weight || {},
    };

    customItemWithFallbacks.unitPrice.code = customItemWithFallbacks.unitPrice.code || defaultCurrency;
    customItemWithFallbacks.weight.unit = customItemWithFallbacks.weight.unit || weightUnit;

    return customItemWithFallbacks;
  });
};

const customsWithDefaultsSelector = createSelector(
  (customs) => customs,
  (_, defaultCustomsData) => defaultCustomsData.content,
  (_, defaultCustomsData) => defaultCustomsData.nonDelivery,
  (_, defaultCustomsData) => defaultCustomsData.exportDeclarationNumber,
  (_, defaultCustomsData) => defaultCustomsData.invoiceNumber,
  (_, defaultCustomsData) => defaultCustomsData.exemptionCodeOrITN,
  (_, __, defaultCurrency) => defaultCurrency,
  (_, __, ___, defaultWeightUnit) => defaultWeightUnit,
  (
    customs,
    defaultCustomsContent,
    defaultCustomsNonDelivery,
    defaultExportDeclarationNumber,
    defaultInvoiceNumber,
    defaultExemptionCodeOrITN,
    defaultCurrency,
    defaultWeightUnit,
  ) => ({
    ...customs,
    customsItems: customsItemsWithDefaults(customs.customsItems, defaultCurrency, defaultWeightUnit),
    exemptionCodeOrITN: customs.exemptionCodeOrITN || defaultExemptionCodeOrITN,
    content: customs.content || defaultCustomsContent,
    nonDelivery: customs.nonDelivery || defaultCustomsNonDelivery,
    exportDeclarationNumber: customs.exportDeclarationNumber || defaultExportDeclarationNumber,
    invoiceNumber: customs.invoiceNumber || defaultInvoiceNumber,
  }),
);

class CustomsServiceClass {
  customsHasErrors(customs, includeHarmonizedTariffCode = false) {
    if (!customs) {
      return true;
    }
    const hasTopLevelError = !customs.content || !customs.nonDelivery;
    const requiredItemFields = getRequiredItemFields(includeHarmonizedTariffCode);
    const hasDeclarationError = customs.customsItems.reduce((sum, item) => {
      const missingRequiredField = requiredItemFields.some((field) => !get(item, field));
      const hasValue = get(item, 'unitPrice.value', 0) > 0;
      return sum || missingRequiredField || !hasValue;
    }, false);

    const hasNoItems = customs.customsItems.length === 0;

    return hasTopLevelError || hasDeclarationError || hasNoItems;
  }

  fillCustomsWithDefaults(customs, defaultCustomsData, defaultCurrency, defaultWeightUnit) {
    if (!customs) {
      return customs;
    }
    return customsWithDefaultsSelector(customs, defaultCustomsData, defaultCurrency, defaultWeightUnit);
  }

  validateCustoms(customs, defaultCountryCode, defaultCurrencyCode, defaultWeightUnit) {
    const emptyDeclaration = () =>
      emptyCustomsItem({
        defaultCountryCode,
        currencyCode: defaultCurrencyCode,
        weightUnit: defaultWeightUnit,
      });

    const validatedCustoms = {
      content: '',
      nonDelivery: '',
      dutiesPaid: null,
      postagePaid: null,
      exportDeclarationNumber: '',
      invoiceNumber: '',
      customsItems: [emptyDeclaration()],
      ...customs,
    };

    if (!validatedCustoms.customsItems) {
      validatedCustoms.customsItems = [];
    }

    if (!validatedCustoms.customsItems.length) {
      validatedCustoms.customsItems.push(emptyDeclaration());
    }

    validatedCustoms.customsItems = validatedCustoms.customsItems.map((customsItem) => {
      if (!customsItem.declarationKey) {
        return {
          ...customsItem,
          declarationKey: customsItem.customsItemId,
        };
      }
      return customsItem;
    });

    return validatedCustoms;
  }

  restoreCustomsToNotNeededState(customs, needsCustoms) {
    if (needsCustoms) {
      return customs;
    }
    return {
      content: null,
      nonDeivery: null,
      exemptionCodeOrITN: '',
      postagePaid: { value: 0, code: customs?.postagePaid?.code ?? 'USD' },
      dutiesPaid: { value: 0, code: customs?.dutiesPaid?.code ?? 'USD' },
      customsItems: [],
    };
  }
}

export const CustomsService = new CustomsServiceClass();
